import { Drawer } from '@material-ui/core';
import { isEmpty } from 'lodash-es';
import React, { JSX, useState } from 'react';

import { CloseButton } from '~/src/dash/components/buttons/commonButtons/CloseButton';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { highlightedDivStyle } from '~/src/dash/components/layout/common';
import {
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  borderRadius,
  ButtonV2,
  Col,
  Cols,
  Panel,
  Row,
  Rows,
  spacing,
  Text,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState } from '~/src/dash/utils/loading-states';
import { useBaseState } from '~/src/dash/views/base/contexts';
import { AdBreakTable } from '~/src/dash/views/inventory-rules/AllRules/AdBreakDrawer/AdBreakTable';
import { adBreakTableConfig } from '~/src/dash/views/inventory-rules/AllRules/AdBreakDrawer/config';
import {
  convertRuleTypeToString,
  IAdBreak,
  RuleType,
} from '~/src/dash/views/inventory-rules/common';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  drawerOverview: Record<string, string>;
  drawerTime: Record<string, string>;
  drawerContent: Record<string, string>;
  drawerCustom: Record<string, string>;
  drawerTableData: IAdBreak[];
  sortedRows: IListViewTableRow[];
  drawerLoadState: LoadingState;
  handleClickNext: (index: number) => void;
  handleClickPrev: (index: number) => void;
  handleOnClickColumnHeader: (column: IListViewTableColumnInfo) => void;
  visibleDrawerRows: IListViewTableRow[];
  handleChangePage: (event: React.MouseEvent | null, page: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  page: number;
  rowsPerPage: RowsPerPage;
}

export const AdBreakDrawer = ({
  isOpen,
  onClose,
  drawerOverview,
  drawerTime,
  drawerContent,
  drawerCustom,
  drawerTableData,
  sortedRows,
  drawerLoadState,
  handleClickNext,
  handleClickPrev,
  handleOnClickColumnHeader,
  handleChangePage,
  handleChangeRowsPerPage,
  visibleDrawerRows,
  order,
  orderBy,
  page,
  rowsPerPage,
}: IProps): JSX.Element => {
  const { totalWidth } = useBaseState();
  const cfg = adBreakTableConfig;
  const { t } = usePublicaTranslation();

  const [isOverviewOpen, setIsOverviewOpen] = useState(true);

  // Calculate current index once to avoid multiple findIndex calls
  const currentIndex = sortedRows.findIndex((rule) => String(rule.ID.value) === drawerOverview.ID);
  const canNavigatePrev = currentIndex > 0;
  const canNavigateNext = currentIndex >= 0 && currentIndex < sortedRows.length - 1;

  return (
    <Drawer open={isOpen} onClose={onClose} anchor='right'>
      <Panel basePadding style={{ width: `${(totalWidth || 800) * 0.6}px`, minWidth: '600px' }}>
        <Rows gap={spacing.small}>
          <Row noPadding marginBottom={spacing.small}>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              {/* TITLE */}
              <Col>
                <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                  <Col displayFlex>
                    <IconV2 name='channels' large />
                  </Col>
                  <Col>
                    <Text large bold tKey='AdBreakDetails' />
                  </Col>
                </Cols>
              </Col>
              <Col>
                <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                  {/* PREV/NEXT BUTTONS */}
                  <Col>
                    <Cols gap={spacing.extraSmall}>
                      <Col displayFlex>
                        <ButtonV2
                          small
                          style={{ paddingLeft: '12px' }}
                          clearBackground
                          disabled={!canNavigatePrev}
                          onClick={() => {
                            if (canNavigatePrev) {
                              handleClickPrev(currentIndex);
                            }
                          }}
                        >
                          <IconV2
                            style={{
                              display: 'flex',
                              transform: 'rotate(90deg)',
                            }}
                            name='caret'
                          />
                        </ButtonV2>
                      </Col>
                      <Col displayFlex>
                        <ButtonV2
                          small
                          style={{ paddingRight: '12px' }}
                          clearBackground
                          disabled={!canNavigateNext}
                          onClick={() => {
                            if (canNavigateNext) {
                              handleClickNext(currentIndex);
                            }
                          }}
                        >
                          <IconV2
                            name='caret'
                            style={{ display: 'flex', transform: 'rotate(270deg)' }}
                          />
                        </ButtonV2>
                      </Col>
                    </Cols>
                  </Col>
                  {/* CLOSE BUTTON */}
                  <Col>
                    <CloseButton onClick={onClose} />
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Row>
          <Row noPadding>
            <Rows extraSmallHorizontalPadding smallVerticalPadding style={highlightedDivStyle}>
              <Row noVerticalPadding extraSmallHorizontalPadding>
                <Cols
                  gap={spacing.small}
                  style={{ alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setIsOverviewOpen(!isOverviewOpen)}
                >
                  <Col>
                    <Text small heavy disableSelect tKey='CSVSummary' />
                  </Col>
                  <Col displayFlex>
                    <IconV2
                      name='caret'
                      style={{ transform: isOverviewOpen ? 'rotate(180deg)' : 'none' }}
                    />
                  </Col>
                </Cols>
              </Row>
              {isOverviewOpen && (
                <Row noPadding>
                  <Rows>
                    {!isEmpty(drawerOverview) && (
                      <Row noHorizontalPadding smallVerticalPadding>
                        <Rows>
                          <Row noVerticalPadding extraSmallHorizontalPadding>
                            <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                              <Col displayFlex>
                                <IconV2 name='checkmark-plain' />
                              </Col>
                              <Col>
                                <Text small bold tKey='Overview' />
                              </Col>
                            </Cols>
                          </Row>
                          <Row noPadding paddingLeft='28px'>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: spacing.extraSmall,
                                width: '100%',
                              }}
                            >
                              {Object.entries(drawerOverview).map(
                                ([key, value]) =>
                                  key !== 'ID' && (
                                    <div key={key}>
                                      <Rows>
                                        <Row noPadding>
                                          <Text extraSmall bold color={publicaColors.gray}>
                                            {key}:
                                          </Text>
                                        </Row>
                                        <Row noPadding>
                                          <Text small>{value}</Text>
                                        </Row>
                                      </Rows>
                                    </div>
                                  ),
                              )}
                            </div>
                          </Row>
                        </Rows>
                      </Row>
                    )}
                    {!isEmpty(drawerTime) && (
                      <Row
                        noHorizontalPadding
                        smallVerticalPadding
                        style={{ borderTop: `0.5px solid ${publicaColors.lightGray}` }}
                      >
                        <Rows>
                          <Row noVerticalPadding extraSmallHorizontalPadding>
                            <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                              <Col displayFlex>
                                <IconV2 name='clock' />
                              </Col>
                              <Col>
                                <Text small bold tKey='Time' />
                              </Col>
                            </Cols>
                          </Row>
                          <Row noPadding paddingLeft='28px'>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: spacing.extraSmall,
                                width: '100%',
                              }}
                            >
                              {Object.entries(drawerTime).map(([key, value]) => (
                                <div key={key}>
                                  <Rows>
                                    <Row noPadding>
                                      <Text extraSmall bold color={publicaColors.gray}>
                                        {key}:
                                      </Text>
                                    </Row>
                                    <Row noPadding>
                                      <Text small>{value}</Text>
                                    </Row>
                                  </Rows>
                                </div>
                              ))}
                            </div>
                          </Row>
                        </Rows>
                      </Row>
                    )}
                    {!isEmpty(drawerContent) && (
                      <Row
                        noHorizontalPadding
                        smallVerticalPadding
                        style={{ borderTop: `0.5px solid ${publicaColors.lightGray}` }}
                      >
                        <Rows>
                          <Row noVerticalPadding extraSmallHorizontalPadding>
                            <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                              <Col displayFlex>
                                <IconV2 name='circle-play' />
                              </Col>
                              <Col>
                                <Text small bold tKey='Content' />
                              </Col>
                            </Cols>
                          </Row>
                          <Row noPadding paddingLeft='28px'>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: spacing.extraSmall,
                                width: '100%',
                              }}
                            >
                              {Object.entries(drawerContent).map(([key, value]) => (
                                <div key={key}>
                                  <Rows>
                                    <Row noPadding>
                                      <Text extraSmall bold color={publicaColors.gray}>
                                        {key}:
                                      </Text>
                                    </Row>
                                    <Row noPadding>
                                      <Text small>{value}</Text>
                                    </Row>
                                  </Rows>
                                </div>
                              ))}
                            </div>
                          </Row>
                        </Rows>
                      </Row>
                    )}
                    {!isEmpty(drawerCustom) && (
                      <Row
                        noHorizontalPadding
                        smallVerticalPadding
                        style={{ borderTop: `0.5px solid ${publicaColors.lightGray}` }}
                      >
                        <Rows>
                          <Row noVerticalPadding extraSmallHorizontalPadding>
                            <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                              <Col displayFlex>
                                <IconV2 name='edit' />
                              </Col>
                              <Col>
                                <Text small bold tKey='Custom' />
                              </Col>
                            </Cols>
                          </Row>
                          <Row noPadding paddingLeft='28px'>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: spacing.extraSmall,
                                width: '100%',
                              }}
                            >
                              {Object.entries(drawerCustom).map(([key, value]) => (
                                <div key={key}>
                                  <Rows>
                                    <Row noPadding>
                                      <Text extraSmall bold color={publicaColors.gray}>
                                        {key}:
                                      </Text>
                                    </Row>
                                    <Row noPadding>
                                      <Text small>{value}</Text>
                                    </Row>
                                  </Rows>
                                </div>
                              ))}
                            </div>
                          </Row>
                        </Rows>
                      </Row>
                    )}
                  </Rows>
                </Row>
              )}
            </Rows>
          </Row>
          {drawerOverview?.[t('FormatType')] === convertRuleTypeToString(RuleType.VMAP) && (
            <Row noPadding>
              <Text
                small
                style={{
                  padding: `${spacing.extraSmall} ${spacing.small}`,
                  backgroundColor: publicaColors.lighterBlue,
                  borderRadius: borderRadius.small,
                  width: 'fit-content',
                }}
                tKey='AdBreakTooltipBreakdown'
              />
            </Row>
          )}
          {drawerOverview?.[t('FormatType')] === convertRuleTypeToString(RuleType.VMAP) && (
            <Row noPadding>
              <AdBreakTable
                cfg={cfg}
                data={drawerTableData}
                loadState={drawerLoadState}
                handleOnClickColumnHeader={handleOnClickColumnHeader}
                visibleDrawerRows={visibleDrawerRows}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                order={order}
                orderBy={orderBy}
                page={page}
                rowsPerPage={rowsPerPage}
              />
            </Row>
          )}
        </Rows>
      </Panel>
    </Drawer>
  );
};
